/**
 * NumberEditForm.tsx — the set/change write UI for the tenant's WhatsApp
 * `phone_number_id` (Story N2). Mirrors `LeadTransitionForm`'s review-then-
 * confirm shape: a "Review" step reveals a confirmation panel (focus moves
 * to it — WCAG 2.4.3) before `PUT /api/config/numbers` fires.
 *
 * Client-side format check (digits-only, 5-20 chars) is a convenience only —
 * the BFF's 422 is authoritative and is what gets surfaced inline under the
 * field (AC3/AC4): `ApiError.body` carries `{ detail: {field_path, reason}[] }`
 * per `app/clients/capital_tenant_number.py` (verified against
 * `test_numbers_routes.py::test_put_preserves_422_validation_error_shape`).
 * A 409 (`ApiError.detail`, a plain string) already names the conflicting
 * tenant (verified against `test_put_preserves_409_conflict`) and is shown
 * verbatim in a distinct banner (never collapsed into the format error).
 *
 * ADR-078 D3 — the propagation notice is LOCKED, operator-facing copy shown
 * unconditionally on every open of this form AND on the confirm step (AC5).
 * It is not sourced from the API response (a GET never carries one, and a
 * PUT only carries it AFTER the write completes) — this is the SPA's own
 * locked copy of the same notice `app.routes.numbers.PROPAGATION_NOTICE`
 * sends back, so it is visible before the write, not just after.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import floating from '../../lib/floating';

export interface NumberEditFormProps {
  apiClient: ApiClient;
  /** Called after a successful write to refetch the panel's config. */
  onSaved: () => Promise<void>;
  onCancel: () => void;
}

const DIGITS_ONLY_5_20 = /^\d{5,20}$/;

type WriteError =
  | { kind: 'conflict'; message: string }
  | { kind: 'notFound' }
  | { kind: 'unauthorized' }
  | { kind: 'unavailable' };

interface ValidationIssue {
  field_path: string;
  reason: string;
}
/** Distinct write-error rendering (AC4: a 409 names the conflicting tenant). */
const EditWriteError = ({ error }: { error: WriteError }) => {
  const { t } = useTranslation();
  const baseClass =
    'rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40';

  if (error.kind === 'conflict') {
    return (
      <p role="alert" data-testid="number-edit-error-conflict" className={baseClass}>
        {error.message || t('numbers.editForm.errors.conflictFallback')}
      </p>
    );
  }
  if (error.kind === 'notFound') {
    return (
      <p role="alert" data-testid="number-edit-error-notfound" className={baseClass}>
        {t('numbers.editForm.errors.notFound')}
      </p>
    );
  }
  if (error.kind === 'unauthorized') {
    return (
      <p role="alert" data-testid="number-edit-error-unauthorized" className={baseClass}>
        {t('numbers.editForm.errors.unauthorized')}
      </p>
    );
  }
  return (
    <p role="alert" data-testid="number-edit-error-unavailable" className={baseClass}>
      {t('numbers.editForm.errors.unavailable')}
    </p>
  );
};

export const NumberEditForm = ({ apiClient, onSaved, onCancel }: NumberEditFormProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [writeError, setWriteError] = useState<WriteError | null>(null);

  const confirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reviewing) confirmRef.current?.focus();
  }, [reviewing]);

  const validate = (raw: string): string | undefined =>
    DIGITS_ONLY_5_20.test(raw.trim()) ? undefined : t('numbers.editForm.errors.format');

  const canReview = value.trim() !== '' && fieldError == null && !submitting;

  const handleConfirm = async () => {
    const trimmed = value.trim();
    setSubmitting(true);
    setWriteError(null);
    try {
      await apiClient.setNumberConfig({ phone_number_id: trimmed });
      setReviewing(false);
      await onSaved();
    } catch (error) {
      setReviewing(false);
      if (error instanceof ApiError && error.status === 422) {
        const issues = (error.body as { detail?: ValidationIssue[] } | null)?.detail ?? [];
        const issue = issues.find((i) => i.field_path === 'phone_number_id') ?? issues[0];
        setFieldError(issue?.reason ?? t('numbers.editForm.errors.format'));
      } else if (error instanceof ApiError && error.status === 409) {
        setWriteError({ kind: 'conflict', message: error.detail ?? '' });
      } else if (error instanceof ApiError && error.status === 404) {
        setWriteError({ kind: 'notFound' });
      } else if (error instanceof UnauthorizedError) {
        setWriteError({ kind: 'unauthorized' });
      } else {
        setWriteError({ kind: 'unavailable' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      aria-labelledby="number-edit-heading"
      data-testid="number-edit-form"
      className="flex flex-col gap-3 rounded-xl bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10"
      onSubmit={(event) => {
        event.preventDefault();
        if (canReview) setReviewing(true);
      }}
      noValidate
    >
      <h2 id="number-edit-heading" className="text-h3 text-ink-1">
        {t('numbers.editForm.heading')}
      </h2>

      <div className="flex flex-col gap-1">
        <label htmlFor="number-phone-number-id" className="text-meta font-medium text-ink-2">
          {t('numbers.editForm.phoneNumberId')}
        </label>
        <input
          id="number-phone-number-id"
          data-testid="number-phone-number-id"
          type="text"
          inputMode="numeric"
          value={value}
          disabled={submitting}
          aria-invalid={fieldError != null}
          aria-describedby={
            fieldError != null ? 'number-phone-number-id-error' : 'number-phone-number-id-hint'
          }
          onChange={(event) => {
            const next = event.target.value;
            setValue(next);
            setReviewing(false);
            setFieldError(next.trim() === '' ? undefined : validate(next));
          }}
          className="rounded-lg bg-surface-1 px-3 py-1.5 text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus:outline-none focus:ring-2 focus:ring-outline-focus disabled:opacity-60"
        />
        {fieldError != null ? (
          <p
            id="number-phone-number-id-error"
            role="alert"
            data-testid="number-phone-number-id-error"
            className="text-meta text-status-down"
          >
            {fieldError}
          </p>
        ) : (
          <p id="number-phone-number-id-hint" className="text-meta text-ink-3">
            {t('numbers.editForm.phoneNumberIdHint')}
          </p>
        )}
      </div>

      <p
        role="note"
        data-testid="number-propagation-notice"
        className="rounded-lg bg-status-degraded/10 px-3 py-2 text-meta text-ink-2 ring-1 ring-inset ring-status-degraded/30"
      >
        {t('numbers.propagationNotice')}
      </p>

      {!reviewing ? (
        <div className="flex gap-3">
          <button
            type="submit"
            data-testid="number-edit-review"
            disabled={!canReview}
            className="inline-flex w-fit items-center rounded-lg bg-gold max-md:min-h-11 px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90 disabled:opacity-50"
          >
            {t('numbers.editForm.review')}
          </button>
          <button
            type="button"
            data-testid="number-edit-cancel"
            onClick={onCancel}
            className="inline-flex items-center rounded-lg bg-surface-1 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3"
          >
            {t('numbers.editForm.cancel')}
          </button>
        </div>
      ) : null}

      {reviewing ? (
        <div
          ref={confirmRef}
          tabIndex={-1}
          role="group"
          aria-label={t('numbers.editForm.confirmHeading')}
          data-testid="number-edit-confirm"
          className="flex flex-col gap-3 rounded-lg bg-surface-1 p-4 ring-1 ring-inset ring-gold/40 focus:outline-none focus:ring-2 focus:ring-outline-focus"
        >
          <p className="text-body text-ink-1">
            {t('numbers.editForm.confirmBody', { value: value.trim() })}
          </p>
          <p
            role="note"
            data-testid="number-edit-confirm-propagation-notice"
            className="rounded-lg bg-status-degraded/10 px-3 py-2 text-meta text-ink-2 ring-1 ring-inset ring-status-degraded/30"
          >
            {t('numbers.propagationNotice')}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="number-edit-confirm-submit"
              disabled={submitting}
              aria-busy={submitting}
              onClick={() => floating(handleConfirm())}
              className="inline-flex items-center rounded-lg bg-gold max-md:min-h-11 px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90 disabled:opacity-60"
            >
              {submitting ? t('numbers.editForm.submitting') : t('numbers.editForm.confirm')}
            </button>
            <button
              type="button"
              data-testid="number-edit-confirm-back"
              disabled={submitting}
              onClick={() => setReviewing(false)}
              className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
            >
              {t('numbers.editForm.back')}
            </button>
          </div>
        </div>
      ) : null}

      {writeError != null ? <EditWriteError error={writeError} /> : null}
    </form>
  );
};
